
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Eye, CheckCircle, Clock, XCircle, Globe } from "lucide-react";
import { ExpertApplication, PublishedExpert } from "@/types/admin";

interface ApplicationAnalyticsProps {
  applications: ExpertApplication[];
  publishedExperts: PublishedExpert[];
}

export default function ApplicationAnalytics({ applications, publishedExperts }: ApplicationAnalyticsProps) {
  const stats = {
    total: applications.length,
    pending: applications.filter(app => app.status === 'pending').length,
    underReview: 0, // Removed under_review status
    approved: applications.filter(app => app.status === 'approved').length,
    rejected: applications.filter(app => app.status === 'rejected').length,
    published: applications.filter(app => 
      app.status === 'approved' && 
      publishedExperts.some(expert => expert.application_id === app.id && expert.status === 'active')
    ).length,
    approvedButNotPublished: applications.filter(app => 
      app.status === 'approved' && 
      !publishedExperts.some(expert => expert.application_id === app.id && expert.status === 'active')
    ).length
  };

  const conversionRate = stats.total > 0 ? ((stats.published / stats.total) * 100).toFixed(1) : 0;
  const publishingRate = stats.approved > 0 ? ((stats.published / stats.approved) * 100).toFixed(1) : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Applications</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="flex gap-1 mt-2">
            <Badge variant="outline" className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              {stats.pending + stats.underReview} pending
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Published Experts</CardTitle>
          <Globe className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">{stats.published}</div>
          <p className="text-xs text-muted-foreground">
            {conversionRate}% conversion rate
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Approved</CardTitle>
          <CheckCircle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-blue-600">{stats.approved}</div>
          <div className="flex gap-1 mt-2">
            <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
              {stats.published} live
            </Badge>
            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">
              {stats.approvedButNotPublished} pending
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Publishing Rate</CardTitle>
          <Eye className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{publishingRate}%</div>
          <p className="text-xs text-muted-foreground">
            of approved experts are published
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
